import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useBreakpointBoundRef } from '../../hooks/useBreakpointBoundRef';
import { useScrollMainToTop } from '../../hooks/useScrollMainToTop';
import { useMobileSidebarOpen } from '../../hooks/useMobileSidebarOpen';
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed';
import { useViewportAnchoredPanel, USER_MENU_PANEL_OPTIONS, getViewportPanelStyle } from '../../hooks/useViewportAnchoredPanel';
import { useTailwindLg } from '../../hooks/useTailwindLg';
import { useAdminNavigationAnalytics } from '../../hooks/useNavigationAnalytics';
import NotificationDropdown from '../UI/NotificationDropdown';
import PullToRefresh from '../UI/PullToRefresh';
import PasswordChangeModal from '../UI/PasswordChangeModal';
import { staffAPI, authAPI } from '../../services/api';
import {
  Users,
  Clock,
  FileText,
  DollarSign,
  Settings,
  Menu,
  X,
  BarChart3,
  Calendar,
  CalendarDays,
  Shield,
  Home,
  Building,
  BookOpen,
  Key,
  UserPlus,
  Activity,
  Building2,
  Bell,
  User,
  LogOut,
  ChevronDown,
  ArrowLeft,
  Gift,
  GitBranch,
  Briefcase,
  FileBarChart2,
  Bed,
  Server,
  Brain,
  TrendingUp
} from 'lucide-react';
import { getClassificationAvatarHex } from '../../utils/classificationConfig';
import { CountBadge } from '../UI/CountBadge';
import { useExceptionsPendingCount } from '../../hooks/useAttendanceData';
import { rankNavigationItems } from '../../utils/navigationRanking';
import weplLogo from '../../assets/images/wepl_logo.png';

const AdminLayout = ({ children }) => {
  const { user, logout, canAccessAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen, onOverlayClick, menuButtonProps } = useMobileSidebarOpen();
  const [sidebarCollapsed, setSidebarCollapsed] = useSidebarCollapsed(user?.staff_id || user?.id);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState(null);
  const { notifications, unreadCount, loading, error, markAsRead, markAllAsRead } = useNotifications();
  const userMenuRef = useClickOutside(() => setUserMenuOpen(false), userMenuOpen);
  const notificationRef = useClickOutside(() => setNotificationOpen(false), notificationOpen);
  const isLg = useTailwindLg();
  const {
    bindMobile: bindMobileUserMenuRef,
    bindDesktop: bindDesktopUserMenuRef,
    visibleRef: userMenuViewportAnchorRef,
  } = useBreakpointBoundRef(isLg, userMenuRef);
  const {
    bindMobile: bindMobileNotificationRef,
    bindDesktop: bindDesktopNotificationRef,
  } = useBreakpointBoundRef(isLg, notificationRef);
  const { panelRect: userMenuPanelRect } = useViewportAnchoredPanel({
    open: userMenuOpen,
    anchorRef: userMenuViewportAnchorRef,
    ...USER_MENU_PANEL_OPTIONS,
  });

  const toggleNotifications = useCallback(() => {
    setUserMenuOpen(false);
    setNotificationOpen((open) => !open);
  }, []);
  const mainScrollRef = useRef(null);
  const scrollMainToTop = useScrollMainToTop(mainScrollRef);

  // Pending attendance exceptions count for sidebar badge
  // MUST be called before any early returns to comply with Rules of Hooks
  const { data: pendingExceptionsCount = 0 } = useExceptionsPendingCount();

  // Pending leave applications count for sidebar badge
  // MUST be called before any early returns to comply with Rules of Hooks
  const { data: pendingLeaveData } = useQuery({
    queryKey: ['leave', 'pendingCount'],
    queryFn: () => staffAPI.getPendingLeaveCount(),
    enabled: !!user && canAccessAdmin(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    select: (response) => response.data?.count ?? 0,
  });
  const pendingLeaveCount = pendingLeaveData || 0;

  // Handle voluntary password change
  const handlePasswordChange = async (currentPassword, newPassword) => {
    setPasswordChangeLoading(true);
    setPasswordChangeError(null);
    try {
      const response = await authAPI.changePassword(currentPassword, newPassword);
      if (response?.data?.success || response?.status === 200) {
        setPasswordModalOpen(false);
        toast.success('Password changed successfully! You have been logged out of other devices.');
      } else {
        setPasswordChangeError('Failed to change password. Please try again.');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to change password. Please try again.';
      setPasswordChangeError(errorMessage);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Default order is the cold-start fallback. Daily operations and system
  // administration remain familiar anchors; usage personalises the middle.
  const defaultAdminNavigation = useMemo(() => [
    {
      analyticsKey: 'admin-dashboard',
      name: 'Admin Dashboard',
      href: '/admin',
      icon: BarChart3,
      description: 'Overview & Analytics'
    },
    {
      analyticsKey: 'leave-management',
      name: 'Leave Management',
      href: '/admin/leave?tab=applications',
      icon: Calendar,
      description: 'Applications, Balances & Reporting',
      badge: pendingLeaveCount,
    },
    {
      analyticsKey: 'attendance-management',
      name: 'Attendance Management',
      href: '/admin/attendance',
      icon: Clock,
      description: 'Tracking, Exceptions & Compliance',
      badge: pendingExceptionsCount,
    },
    {
      analyticsKey: 'overtime-monitoring',
      name: 'Overtime Monitoring',
      href: '/admin/attendance/overtime-monitoring',
      icon: Activity,
      description: '72-Hour Compliance & Alert Management'
    },
    {
      analyticsKey: 'rest-day-monitoring',
      name: 'Rest Day Monitoring',
      href: '/admin/attendance/rest-day-monitoring',
      icon: CalendarDays,
      description: '12-Day Rest Day Interval Compliance'
    },
    {
      analyticsKey: 'employee-management',
      name: 'Employee Management',
      href: '/admin/employees',
      icon: Users,
      description: 'Manage Staff Profiles'
    },
    {
      analyticsKey: 'document-requests',
      name: 'Document Requests',
      href: '/admin/document-requests',
      icon: FileText,
      description: 'HR Letters & Verifications'
    },
    {
      analyticsKey: 'cv-generation',
      name: 'CV Generation',
      href: '/admin/cv-generation',
      icon: Briefcase,
      description: 'CVs, Projects & Qualifications'
    },
    {
      analyticsKey: 'payroll-management',
      name: 'Payroll Management',
      href: '/admin/payroll',
      icon: DollarSign,
      description: 'Processing, Compliance & Reports'
    },
    {
      analyticsKey: 'bik-management',
      name: 'BIK Management',
      href: '/admin/bik-management',
      icon: Gift,
      description: 'Benefits in Kind & IR8A'
    },
    {
      analyticsKey: 'reports',
      name: 'Reports',
      href: '/admin/reports',
      icon: FileBarChart2,
      description: 'Analytics & Insights'
    },
    {
      analyticsKey: 'government-reimbursement',
      name: 'Government Reimbursement',
      href: '/admin/government-reimbursement',
      icon: Shield,
      description: 'Government-Paid Leave, Levy Waivers & NS Make-Up Pay'
    },
    {
      analyticsKey: 'learning-development',
      name: 'Learning & Development',
      href: '/admin/learning-development',
      icon: BookOpen,
      description: 'Training, Certifications & Employee Development'
    },
    {
      analyticsKey: 'talent-insights',
      name: 'Talent Insights',
      href: '/admin/talent-insights',
      icon: Brain,
      description: 'Big Five Profile & Future Psychometric Assessments'
    },
    {
      analyticsKey: 'employee-lifecycle',
      name: 'Employee Lifecycle Management',
      href: '/admin/employee-lifecycle',
      icon: UserPlus,
      description: 'Onboarding and Offboarding'
    },
    {
      analyticsKey: 'recruitment',
      name: 'Recruitment',
      href: '/admin/recruitment',
      icon: Users,
      description: 'Applicant Tracking & Hiring Pipeline'
    },
    {
      analyticsKey: 'approval-workflows',
      name: 'Approval Workflows',
      href: '/admin/approval-workflows',
      icon: GitBranch,
      description: 'Approval Chains & Assignments'
    },
    {
      analyticsKey: 'employee-handbook',
      name: 'Employee Handbook',
      href: '/admin/handbook',
      icon: BookOpen,
      description: 'Edit Policies with Audit Trail'
    },
    {
      analyticsKey: 'policy-acknowledgements',
      name: 'Policy Acknowledgements',
      href: '/admin/policies/acknowledgements',
      icon: Shield,
      description: 'Track Code of Conduct Sign-offs'
    },
    {
      analyticsKey: 'department-management',
      name: 'Department Management',
      href: '/admin/departments',
      icon: Building2,
      description: 'Organizational Structure'
    },
    {
      analyticsKey: 'site-management',
      name: 'Site Management',
      href: '/admin/site',
      icon: Building,
      description: 'Manage project sites and work locations'
    },
    {
      analyticsKey: 'dormitory-management',
      name: 'Dormitory Management',
      href: '/admin/dormitory-management',
      icon: Bed,
      description: 'Locations, Assignments & Costs'
    },
    {
      analyticsKey: 'notification-log',
      name: 'Notification Log',
      href: '/admin/notification-log',
      icon: Bell,
      description: 'Notification & Delivery Audit'
    },
    {
      analyticsKey: 'system-monitoring',
      name: 'System Monitoring',
      href: '/admin/system-monitoring',
      icon: Server,
      description: 'Tasks, Health Checks & Audit Trail'
    },
    {
      analyticsKey: 'usage-analytics',
      name: 'Usage Analytics',
      href: '/admin/usage',
      icon: TrendingUp,
      description: 'Platform Usage & Adoption'
    },
    {
      analyticsKey: 'system-settings',
      name: 'System Settings',
      href: '/admin/settings',
      icon: Settings,
      description: 'App Configuration'
    },
    // HR-admin only — mirrors backend IsHRAdmin (has_admin_access).
    // Worker-admins can reach other admin pages but not mint/manage global API keys.
    ...(user?.has_admin_access || user?.is_superuser ? [{
      analyticsKey: 'api-operations',
      name: 'API Operations',
      href: '/admin/external-tokens',
      icon: Key,
      description: 'External API health, keys & request logs'
    }] : []),
  ], [
    pendingExceptionsCount,
    pendingLeaveCount,
    user?.has_admin_access,
    user?.is_superuser,
  ]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isCurrentPath = (path) => {
    // Strip query string and hash from the nav item's href so the active-state
    // check works for entries like "/admin/leave?tab=applications".
    // (location.pathname never contains the query string.)
    const basePath = path.split('?')[0].split('#')[0];

    if (basePath === '/admin') {
      // Match both /admin and /admin/dashboard as they both show AdminDashboard
      return location.pathname === '/admin' || location.pathname === '/admin/dashboard';
    }

    // For nested routes, be more specific to avoid conflicts
    if (basePath === '/admin/attendance') {
      // Only match /admin/attendance exactly, not /admin/attendance/*
      return location.pathname === '/admin/attendance' ||
             (location.pathname.startsWith('/admin/attendance/') &&
              !location.pathname.includes('/overtime-monitoring') &&
              !location.pathname.includes('/rest-day-monitoring'));
    }

    if (basePath === '/admin/attendance/overtime-monitoring') {
      // Only match the overtime monitoring page
      return location.pathname === '/admin/attendance/overtime-monitoring';
    }

    if (basePath === '/admin/attendance/rest-day-monitoring') {
      // Only match the rest day monitoring page
      return location.pathname === '/admin/attendance/rest-day-monitoring';
    }

    // For other paths, use startsWith but be more careful
    return location.pathname === basePath || location.pathname.startsWith(basePath + '/');
  };

  const currentNavigationItemKey = defaultAdminNavigation.find(
    (item) => isCurrentPath(item.href)
  )?.analyticsKey;
  const hasFullAdminAccess = canAccessAdmin();
  const isFinancePayrollUser = (
    !hasFullAdminAccess
    && user?.portal_groups?.includes('Finance')
  );
  const { data: navigationRanking } = useAdminNavigationAnalytics({
    currentItemKey: currentNavigationItemKey,
    userIdentifier: user?.staff_id || user?.id,
    enabled: !!user && hasFullAdminAccess,
  });
  const adminNavigation = useMemo(
    () => (
      isFinancePayrollUser
        ? defaultAdminNavigation.filter(
          (item) => item.analyticsKey === 'payroll-management',
        ).map((item) => ({
          ...item,
          href: '/admin/payroll?tab=processing&step=4',
          description: 'Payment files & payroll review',
        }))
        : rankNavigationItems(
          defaultAdminNavigation,
          navigationRanking?.scores,
          [
            'admin-dashboard',
            'leave-management',
            'attendance-management',
            'overtime-monitoring',
            'rest-day-monitoring',
            'employee-management',
          ],
          [
            'notification-log',
            'system-monitoring',
            'usage-analytics',
            'system-settings',
            'api-operations',
          ],
        )
    ),
    [defaultAdminNavigation, isFinancePayrollUser, navigationRanking?.scores]
  );

  // Redirect only after all hooks have run.
  if (!hasFullAdminAccess && !isFinancePayrollUser) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="h-dvh bg-gray-50 flex overflow-hidden">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onOverlayClick} />
          <div className="fixed top-0 left-0 w-80 h-full bg-white shadow-xl">
            <SidebarContent 
              navigation={adminNavigation} 
              isCurrentPath={isCurrentPath}
              user={user}
              onLogout={handleLogout}
              onClose={() => setSidebarOpen(false)}
              collapsed={false}
            />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className={`hidden lg:flex ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-80'} lg:flex-col lg:flex-shrink-0 transition-all duration-300`}>
        <SidebarContent 
          navigation={adminNavigation} 
          isCurrentPath={isCurrentPath}
          user={user}
          onLogout={handleLogout}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header — fixed inside the locked shell. paddingTop respects the
            iOS notch / Android cutout in PWA standalone mode. touchAction:
            'manipulation' blocks pan gestures so the page can never be dragged
            from the top bar. Empty-area tap → scroll <main> to top (mobile-only
            DS standard — useScrollMainToTop / App Shell). */}
        <div
          onClick={scrollMainToTop}
          className="lg:hidden bg-white shadow-sm border-b px-4 flex items-center justify-between flex-shrink-0"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            height: 'calc(4rem + env(safe-area-inset-top))',
            touchAction: 'manipulation',
          }}
        >
          <div className="flex items-center space-x-2 shrink-0">
            <button
              {...menuButtonProps}
              className="icon-button-admin"
            >
              <Menu className="w-6 h-6" />
            </button>
            <Link to="/" className="flex items-center shrink-0" aria-label="Go to dashboard">
              <img src={weplLogo} alt="Company Logo" className="h-7 w-auto sm:h-8" />
            </Link>
          </div>
          
          {/* Mobile User Profile */}
          <div className="flex items-center space-x-2">
            {/* Notifications — click-outside ref wraps bell + panel (DS dismissal pattern) */}
            <div className="relative" ref={bindMobileNotificationRef}>
              <button 
                type="button"
                onClick={toggleNotifications}
                className="icon-button-admin relative"
                title="Notifications"
                aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
                aria-expanded={notificationOpen}
                aria-haspopup="dialog"
              >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </button>

              {notificationOpen && !isLg && (
                <NotificationDropdown
                  notifications={notifications}
                  unreadCount={unreadCount}
                  loading={loading}
                  error={error}
                  onClose={() => setNotificationOpen(false)}
                  onMarkAsRead={markAsRead}
                  onMarkAllAsRead={markAllAsRead}
                />
              )}
            </div>

            {/* User Menu */}
            <div className="relative shrink-0" ref={bindMobileUserMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationOpen(false);
                  setUserMenuOpen((open) => !open);
                }}
                className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 transition-all duration-300"
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                  style={{ 
                    backgroundColor: getClassificationAvatarHex(user?.employment_classification)
                  }}
                  title={`Classification: ${user?.employment_classification || 'Unknown'}`}
                >
                  <span className="text-xs font-semibold text-white">
                    {user?.full_name?.charAt(0) || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {/* User Dropdown — mount only on the visible header so click-outside
                  cannot tear the menu down before Sign out / row actions fire. */}
              {userMenuOpen && !isLg && (
                <div
                  className="fixed z-[70] rounded-2xl border border-gray-200 bg-white py-2 shadow-lg"
                  style={getViewportPanelStyle(userMenuPanelRect)}
                >
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-700">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user?.staff_id || '--'}
                    </p>
                  </div>
                  <div className="px-2 space-y-0.5">
                    <Link
                      to="/profile"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4 mr-3 shrink-0" />
                      Profile
                    </Link>
                    <Link
                      to="/dashboard"
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Home className="w-4 h-4 mr-3 shrink-0" />
                      User Dashboard
                    </Link>
                  </div>
                  <div className="border-t border-gray-100 my-2 mx-3" />
                  <div className="px-2 pb-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                    >
                      <LogOut className="w-4 h-4 mr-3 shrink-0" />
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desktop header — scroll-to-top is mobile-only (see useScrollMainToTop). */}
        <div
          className="hidden lg:flex bg-white border-b px-4 lg:px-6 h-16 items-center justify-between flex-shrink-0"
        >
            {/* Left Section: Logo */}
            <div className="flex items-center space-x-6">
              <Link to="/" className="flex items-center shrink-0" aria-label="Go to dashboard">
                <img src={weplLogo} alt="Company logo" className="h-7 w-auto" />
              </Link>
            </div>

            {/* Right Section: User Profile */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Notifications — click-outside ref wraps bell + panel (DS dismissal pattern) */}
              <div className="relative" ref={bindDesktopNotificationRef}>
                <button
                  type="button"
                  onClick={toggleNotifications}
                  className="icon-button-admin relative"
                  title="Notifications"
                  aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications'}
                  aria-expanded={notificationOpen}
                  aria-haspopup="dialog"
                >
                  <Bell className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>

                {notificationOpen && isLg && (
                  <NotificationDropdown
                    notifications={notifications}
                    unreadCount={unreadCount}
                    loading={loading}
                    error={error}
                    onClose={() => setNotificationOpen(false)}
                    onMarkAsRead={markAsRead}
                    onMarkAllAsRead={markAllAsRead}
                  />
                )}
              </div>

              {/* User Profile */}
              <div className="relative shrink-0" ref={bindDesktopUserMenuRef}>
                <button
                  type="button"
                  onClick={() => {
                    setNotificationOpen(false);
                    setUserMenuOpen((open) => !open);
                  }}
                  className="flex items-center space-x-3 p-2 rounded-full hover:bg-gray-100 transition-all duration-300"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="menu"
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center shadow-sm"
                    style={{ 
                      backgroundColor: getClassificationAvatarHex(user?.employment_classification)
                    }}
                    title={`Classification: ${user?.employment_classification || 'Unknown'}`}
                  >
                    <span className="text-sm font-semibold text-white">
                      {user?.full_name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-700">
                      {user?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-gray-500 font-mono">
                      {user?.staff_id || '--'}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>

                {/* User Dropdown — desktop copy only while lg is visible. */}
                {userMenuOpen && isLg && (
                  <div
                    className="fixed z-[70] rounded-2xl border border-gray-200 bg-white py-2 shadow-lg"
                    style={getViewportPanelStyle(userMenuPanelRect)}
                  >
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-700">
                        {user?.full_name || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        {user?.staff_id || '--'}
                      </p>
                      {user?.work_email && (
                        <p className="text-xs text-gray-400 mt-1 break-all">
                          {user.work_email}
                        </p>
                      )}
                    </div>
                    
                    {/* Role Switching */}
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-1">Switch Role</p>
                      <Link
                        to="/dashboard"
                        className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-all duration-300 group"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-3 shrink-0 text-gray-400 group-hover:text-blue-600" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium">Employee View</p>
                          <p className="text-xs text-gray-500">Access your personal dashboard</p>
                        </div>
                        <ArrowLeft className="w-4 h-4 shrink-0 text-gray-400" />
                      </Link>
                    </div>
                    
                    <div className="border-t border-gray-100 my-2 mx-3" />
                    
                    <div className="px-2 space-y-0.5">
                      <Link
                        to="/profile"
                        className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4 mr-3 shrink-0" />
                        My Profile
                      </Link>
                      
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          setPasswordModalOpen(true);
                        }}
                        className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      >
                        <Key className="w-4 h-4 mr-3 shrink-0" />
                        Change Password
                      </button>
                    </div>
                    
                    <div className="border-t border-gray-100 my-2 mx-3" />
                    <div className="px-2 pb-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setUserMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center w-full px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-300"
                      >
                        <LogOut className="w-4 h-4 mr-3 shrink-0" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
        </div>

        {/* Page content - Scrollable */}
        <main ref={mainScrollRef} className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-4 md:p-6 pb-safe" style={{ touchAction: 'pan-y' }}>
          <PullToRefresh scrollContainerRef={mainScrollRef}>
            {children}
          </PullToRefresh>
        </main>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={passwordModalOpen}
        onChangePassword={handlePasswordChange}
        onClose={() => {
          setPasswordModalOpen(false);
          setPasswordChangeError(null);
        }}
        loading={passwordChangeLoading}
        error={passwordChangeError}
        mode="voluntary"
      />
    </div>
  );
};

const SidebarContent = ({ navigation, isCurrentPath, user, onLogout, onClose, collapsed, onToggleCollapse }) => {
  return (
    <div className="flex flex-col h-full bg-white border-r border-gray-200">
      {/* Header - Fixed */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-6'} h-16 border-b border-gray-200 flex-shrink-0`}>
        {collapsed ? (
          /* Collapsed state - Only show hamburger menu */
          <button 
            onClick={onToggleCollapse} 
            className="icon-button-admin"
            title="Expand sidebar"
          >
            <Menu className="w-6 h-6" />
          </button>
        ) : (
          /* Expanded state - Show logo, text, and controls */
          <>
            <div className="flex items-center">
              <img 
                src={weplLogo} 
                alt="Company Logo" 
                className="h-7 w-auto object-contain"
              />
              <div className="ml-3">
                <h2 className="text-lg font-bold text-gray-900">Admin Portal</h2>
                <p className="text-xs text-gray-500 font-medium">HR Management System</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onToggleCollapse && (
                <button 
                  onClick={onToggleCollapse} 
                  className="icon-button-admin hidden lg:inline-flex"
                  title="Collapse sidebar"
                >
                  <Menu className="w-5 h-5" />
                </button>
              )}
              {onClose && (
                <button onClick={onClose} className="icon-button-admin lg:hidden">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Navigation - Scrollable */}
      <nav className={`flex-1 ${collapsed ? 'px-2' : 'px-4'} py-2 space-y-2 overflow-y-auto`}>
        {navigation.map((item) => {
          const isActive = isCurrentPath(item.href);
          return (
            <Link
              key={item.analyticsKey}
              to={item.href}
              onClick={onClose}
              className={`flex items-center rounded-full transition-all duration-300 group relative ${
                isActive
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              } ${collapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'}`}
              title={collapsed ? item.name : undefined}
            >
              {item.icon === 'logo' ? (
                <img
                  src={weplLogo}
                  alt="WEPL Logo"
                  className={`w-6 h-6 ${collapsed ? '' : 'mr-4'} object-contain ${
                    isActive ? 'brightness-0 invert' : 'brightness-75'
                  } transition-all duration-300`}
                />
              ) : (
                <item.icon
                  className={`w-6 h-6 ${collapsed ? '' : 'mr-4'} flex-shrink-0 ${
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'
                  } transition-colors duration-300`}
                />
              )}
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-gray-900'} transition-colors duration-300`}>
                    {item.name}
                  </p>
                  <p className={`text-xs mt-1 ${
                    isActive ? 'text-red-100' : 'text-gray-500'
                  } transition-colors duration-300`}>
                    {item.description}
                  </p>
                </div>
              )}
              {item.badge > 0 && (
                <CountBadge
                  count={item.badge}
                  theme={isActive ? 'admin' : 'orange'}
                  active={isActive}
                  overlay={collapsed}
                  overlayPosition={collapsed ? 'inside' : 'outside'}
                  className={collapsed ? '' : 'ml-2'}
                />
              )}

            </Link>
          );
        })}
      </nav>

      {/* Back to HR Portal - Footer */}
      <div className={`${collapsed ? 'px-2' : 'px-4'} py-4 border-t border-gray-200`}>
        <Link
          to="/dashboard"
          onClick={onClose}
          className={`flex items-center rounded-full transition-all duration-300 group ${
            collapsed ? 'justify-center w-11 h-11 mx-auto' : 'w-full px-4 py-3'
          } bg-gradient-to-r from-blue-900 to-blue-800 text-white shadow-lg hover:shadow-xl`}
          title={collapsed ? 'Back to HR Portal' : undefined}
        >
          <Home
            className={`w-5 h-5 ${collapsed ? '' : 'mr-3'} flex-shrink-0 text-white transition-colors duration-300`}
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white transition-colors duration-300">
                HR Portal
              </p>
              <p className="text-xs mt-0.5 text-blue-100 transition-colors duration-300">
                Employee Dashboard
              </p>
            </div>
          )}
        </Link>
      </div>
    </div>
  );
};

export default AdminLayout; 